import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Send, Printer, Search, Phone, User, Hash, RefreshCw, ArrowLeft, ListOrdered, History, Eye, IndianRupee, CreditCard, Calendar, QrCode, X, Clock, Truck, Store, Edit3, Save, XCircle, Stethoscope, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import Portal from "@/components/Portal";
import { BrandLogo } from "@/components/BrandLogo";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { printNode } from "@/lib/printBill";

const today = () => new Date().toISOString().slice(0, 10);
const money = (n) => `₹ ${Number(n || 0).toFixed(2)}`;
const DEFAULT_GST = 5; // medicines are billed GST-INCLUSIVE at 5% by default

const DURATIONS = [
  { label: "—", days: 0 },
  { label: "3 days", days: 3 },
  { label: "5 days", days: 5 },
  { label: "1 week", days: 7 },
  { label: "10 days", days: 10 },
  { label: "2 weeks", days: 14 },
  { label: "3 weeks", days: 21 },
  { label: "1 month", days: 30 },
  { label: "45 days", days: 45 },
  { label: "2 months", days: 60 },
  { label: "3 months", days: 90 },
];

const addDaysISO = (base, n) => {
  const d = new Date(base);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const emptyRow = () => ({ name: "", qty: 1, price: 0, sku: null, gst_pct: DEFAULT_GST, hsn: "" });

const CONSULT_FEES = [
  { label: "No consultation", amount: 0, type: "" },
  { label: "New consultation · ₹150", amount: 150, type: "new" },
  { label: "Re-consultation · ₹100", amount: 100, type: "re" },
];

const PAY_MODES = [
  { v: "cash", l: "Cash" },
  { v: "upi", l: "UPI" },
  { v: "card", l: "Card" },
  { v: "netbanking", l: "Net banking" },
  { v: "other", l: "Other" },
];

export default function DirectBill() {
  const { branch, user } = useAuth();
  const [tab, setTab] = useState("new");
  const [billNo, setBillNo] = useState("");
  const [patientName, setPatientName] = useState("");
  const [patientPhone, setPatientPhone] = useState("");
  const [mrn, setMrn] = useState("");
  const [items, setItems] = useState([emptyRow()]);
  const [notes, setNotes] = useState("");
  const [customerGstin, setCustomerGstin] = useState("");
  const [duration, setDuration] = useState(DURATIONS[3]); // 1 week default
  const [followUpDate, setFollowUpDate] = useState(addDaysISO(today(), 7));
  const [billType, setBillType] = useState("direct"); // direct | courier
  const [consultFee, setConsultFee] = useState(CONSULT_FEES[0]);
  const [payments, setPayments] = useState([{ mode: "cash", amount: "" }]);
  const [editingBill, setEditingBill] = useState(null);
  const [editReason, setEditReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [recent, setRecent] = useState([]);
  const canEditRole = ["billing", "manager", "admin", "superadmin"].includes(user?.role);

  const fetchSuggested = useCallback(async () => {
    if (!branch?.code) return;
    try {
      const { data } = await api.get("/direct-bills/next-number", { params: { branch_code: branch.code, format: "datetime" } });
      setBillNo(data.suggested);
    } catch (err) {
      toast.error(formatApiError(err, "Could not fetch bill number"));
    }
  }, [branch?.code]);

  const loadRecent = useCallback(async () => {
    if (!branch?.code) return;
    try {
      const { data } = await api.get("/direct-bills", { params: { branch_code: branch.code, date: today(), limit: 20 } });
      setRecent(data);
    } catch { /* silent */ }
  }, [branch?.code]);

  useEffect(() => { fetchSuggested(); loadRecent(); }, [fetchSuggested, loadRecent]);

  useEffect(() => {
    if (duration.days > 0) setFollowUpDate(addDaysISO(today(), duration.days));
  }, [duration]);

  // Totals — prices are inclusive of GST. Consultation fee is GST-exempt and added to the total.
  const totals = useMemo(() => {
    let taxable = 0, cgst = 0, sgst = 0, gross = 0;
    items.forEach((it) => {
      const g = Number(it.qty || 0) * Number(it.price || 0);
      const rate = Number(it.gst_pct || 0);
      const t = rate > 0 ? g / (1 + rate / 100) : g;
      const tax = g - t;
      gross += g;
      taxable += t;
      cgst += tax / 2;
      sgst += tax / 2;
    });
    const fee = Number(consultFee.amount || 0);
    return { taxable: taxable + fee, cgst, sgst, gross, total: gross + fee, fee };
  }, [items, consultFee]);

  const paymentsTotal = useMemo(() => payments.reduce((s, p) => s + (Number(p.amount) || 0), 0), [payments]);
  const paymentsRemaining = Math.max(0, Number((totals.total - paymentsTotal).toFixed(2)));
  const setPayment = (i, patch) => setPayments((prev) => prev.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const addPayment = () => setPayments((prev) => [...prev, { mode: "cash", amount: "" }]);
  const removePayment = (i) => setPayments((prev) => (prev.length === 1 ? [{ mode: "cash", amount: "" }] : prev.filter((_, idx) => idx !== i)));

  const setItem = (i, patch) =>
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const addItem = () => setItems((prev) => [...prev, emptyRow()]);
  const removeItem = (i) =>
    setItems((prev) => (prev.length === 1 ? [emptyRow()] : prev.filter((_, idx) => idx !== i)));

  const resetForm = useCallback(() => {
    setItems([emptyRow()]);
    setPatientName(""); setPatientPhone(""); setMrn(""); setCustomerGstin("");
    setNotes(""); setDuration(DURATIONS[3]);
    setBillType("direct"); setConsultFee(CONSULT_FEES[0]);
    setPayments([{ mode: "cash", amount: "" }]);
    setEditingBill(null); setEditReason("");
  }, []);

  const startEdit = useCallback((bill) => {
    if (!canEditRole) { toast.error("You don't have edit permission"); return; }
    if (bill.archived || bill.verified) { toast.error("Verified or archived bills cannot be edited"); return; }
    setEditingBill(bill);
    setBillNo(bill.bill_no);
    setPatientName(bill.customer_name || "");
    setPatientPhone(bill.customer_phone || "");
    setMrn(bill.mrn || "");
    setCustomerGstin(bill.customer_gstin || "");
    setBillType(bill.bill_type || "direct");
    setConsultFee(CONSULT_FEES.find((c) => c.type === (bill.consultation_type || "")) || (Number(bill.consultation_fee) > 0 ? CONSULT_FEES[1] : CONSULT_FEES[0]));
    const bItems = (bill.items || []).filter((i) => !(i.name || "").toLowerCase().startsWith("consultation"));
    setItems(bItems.length ? bItems.map((i) => ({ name: i.name, qty: i.qty, price: i.price, sku: i.sku || null, gst_pct: i.gst_pct ?? DEFAULT_GST, hsn: i.hsn || "" })) : [emptyRow()]);
    const pays = (bill.payments && bill.payments.length) ? bill.payments.map((p) => ({ mode: p.mode, amount: p.amount })) : [{ mode: bill.payment_mode || "cash", amount: bill.paid || "" }];
    setPayments(pays);
    setDuration(DURATIONS.find((d) => d.label === (bill.duration_label || "—")) || DURATIONS[0]);
    setFollowUpDate(bill.follow_up_date || today());
    setNotes(bill.notes || "");
    setEditReason("");
    setTab("new");
    setReceipt(null);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }, [canEditRole]);

  const cancelEdit = () => { resetForm(); fetchSuggested(); };

  const submit = async () => {
    if (!billNo.trim()) { toast.error("Bill number is required"); return; }
    if (!patientName.trim()) { toast.error("Patient name is required"); return; }
    const clean = items
      .map((it) => ({ name: it.name.trim(), qty: Number(it.qty) || 0, price: Number(it.price) || 0, sku: it.sku || undefined, gst_pct: Number(it.gst_pct) || DEFAULT_GST, hsn: (it.hsn || "").trim() }))
      .filter((it) => it.name && it.qty > 0);
    if (clean.length === 0 && Number(consultFee.amount || 0) <= 0) { toast.error("Add a medicine or a consultation fee"); return; }
    const activePayments = payments
      .map((p) => ({ mode: (p.mode || "cash").toLowerCase(), amount: Number(p.amount || 0) }))
      .filter((p) => p.amount > 0);
    const isEdit = !!editingBill;

    setBusy(true);
    try {
      let data;
      if (isEdit) {
        const patchBody = {
          customer_name: patientName.trim(),
          customer_phone: patientPhone.trim(),
          mrn: mrn.trim().toUpperCase(),
          items: clean,
          notes: notes.trim(),
          duration_label: duration.label === "—" ? "" : duration.label,
          follow_up_days: duration.days,
          follow_up_date: followUpDate || "",
          consultation_fee: Number(consultFee.amount || 0),
          consultation_type: consultFee.type || "",
          payments: activePayments,
          edit_reason: editReason.trim() || "Correction",
        };
        ({ data } = await api.patch(`/direct-bills/${editingBill.id}`, patchBody));
        toast.success(`Bill ${data.bill_no} updated`);
      } else {
        const body = {
          branch_code: branch.code,
          bill_no: billNo.trim(),
          customer_name: patientName.trim(),
          customer_phone: patientPhone.trim(),
          customer_gstin: customerGstin.trim().toUpperCase(),
          mrn: mrn.trim().toUpperCase(),
          items: clean,
          notes: notes.trim(),
          bill_type: billType,
          consultation_fee: Number(consultFee.amount || 0),
          consultation_type: consultFee.type || "",
          payments: activePayments.length ? activePayments : undefined,
          payment_mode: activePayments[0]?.mode || "cash",
          paid: activePayments.length ? activePayments.reduce((s, p) => s + p.amount, 0) : null,
          duration_label: duration.label === "—" ? "" : duration.label,
          follow_up_days: duration.days,
          follow_up_date: followUpDate || "",
        };
        ({ data } = await api.post("/direct-bills", body));
        toast.success(`${billType === "courier" ? "Courier bill" : "Bill"} ${data.bill_no} · ${money(data.total)}`);
      }
      setReceipt(data);
      resetForm();
      fetchSuggested();
      loadRecent();
    } catch (err) {
      toast.error(formatApiError(err, "Could not save bill"));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto animate-fade-up" data-testid="direct-bill-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-[#5C5652] mb-3 ornament-dot">
            Direct bill · walk-in · {branch.name}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-forest mb-1">Direct billing</h1>
          <p className="text-[#5C5652]">Type to find a medicine, print thermal or A4, and mark a follow-up for the marketing team.</p>
        </div>
        <Link to="/billing" data-testid="direct-back-to-billing" className="h-10 px-4 rounded-lg border border-sand bg-white hover:bg-[#F9F6F0] text-sm font-semibold inline-flex items-center gap-2">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.75} /> Consultation bills
        </Link>
      </div>

      {editingBill && (
        <div data-testid="direct-edit-banner" className="mb-4 rounded-lg border border-[#E5A93D] bg-[#FFF7E3] px-4 py-3 flex flex-wrap items-center gap-3">
          <Edit3 className="h-4 w-4 text-[#8B6A1F]" strokeWidth={1.75} />
          <div className="flex-1 text-sm">
            <div className="font-bold text-[#8B6A1F]">Editing bill {editingBill.bill_no}</div>
            <div className="text-xs text-[#8B6A1F]/80">Bill number, GSTIN &amp; bill-type are locked. Changes are logged with your name.</div>
          </div>
          <input
            data-testid="direct-edit-reason"
            value={editReason}
            onChange={(e) => setEditReason(e.target.value)}
            placeholder="Reason (optional)"
            className="h-9 px-3 rounded-md border border-[#E5A93D] bg-white text-sm w-56 focus:outline-none focus:ring-2 focus:ring-[#E5A93D]/40"
          />
          <button onClick={cancelEdit} data-testid="direct-cancel-edit"
            className="h-9 px-3 rounded-md bg-white border border-[#8B6A1F] text-[#8B6A1F] text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1 hover:bg-[#FFF3D6]">
            <XCircle className="h-3.5 w-3.5" /> Cancel edit
          </button>
        </div>
      )}

      <div className="flex bg-[#F9F6F0] border border-sand rounded-lg p-1 mb-6 w-fit">
        {[{ v: "new", l: "New bill", icon: Plus }, { v: "history", l: "Bill history", icon: History }].map((t) => (
          <button key={t.v} onClick={() => setTab(t.v)} data-testid={`direct-tab-${t.v}`}
            className={`px-4 h-9 rounded-md text-sm font-semibold inline-flex items-center gap-2 ${tab === t.v ? "bg-forest text-white" : "text-[#5C5652]"}`}>
            <t.icon className="h-3.5 w-3.5" strokeWidth={1.75} /> {t.l}
          </button>
        ))}
      </div>

      {tab === "new" && (
        <div className="grid gap-6 xl:grid-cols-[1.7fr_1fr]">
          <div className="bg-white border border-sand rounded-lg p-4 sm:p-6" data-testid="direct-bill-form">
            {/* Bill type toggle + consultation fee — top strip */}
            <div className="flex flex-wrap gap-3 mb-4 pb-4 border-b border-dashed border-sand">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#5C5652] mb-1 flex items-center gap-1">
                  Bill type
                </label>
                <div className="inline-flex rounded-md border border-sand overflow-hidden" data-testid="direct-bill-type-tabs">
                  {[{v:"direct",l:"Direct (walk-in)",icon:Store},{v:"courier",l:"Courier",icon:Truck}].map((b) => (
                    <button key={b.v} onClick={() => !editingBill && setBillType(b.v)} disabled={!!editingBill}
                      data-testid={`direct-bill-type-${b.v}`}
                      className={`px-3 h-10 text-sm font-semibold inline-flex items-center gap-2 ${billType === b.v ? "bg-forest text-white" : "bg-white text-[#5C5652] hover:bg-[#F9F6F0]"} ${b.v === "courier" ? "border-l border-sand" : ""} ${editingBill ? "cursor-not-allowed opacity-70" : ""}`}>
                      <b.icon className="h-3.5 w-3.5" strokeWidth={1.75} /> {b.l}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex-1 min-w-[240px]">
                <label className="text-[10px] uppercase tracking-wider text-[#5C5652] mb-1 flex items-center gap-1">
                  <Stethoscope className="h-3.5 w-3.5" strokeWidth={1.75} /> Consultation fee (GST-exempt)
                </label>
                <select data-testid="direct-consult-fee" value={consultFee.label}
                  onChange={(e) => setConsultFee(CONSULT_FEES.find((c) => c.label === e.target.value) || CONSULT_FEES[0])}
                  className="w-full h-10 px-3 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 text-sm">
                  {CONSULT_FEES.map((c) => <option key={c.label} value={c.label}>{c.label}</option>)}
                </select>
              </div>
            </div>

            {/* Top header row: bill no · patient · UHID · phone · gstin — packs into one dense grid */}
            <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              <Field label="Bill number" icon={Hash} testid="direct-bill-no-wrap" className="md:col-span-2 lg:col-span-2">
                <div className="relative">
                  <input data-testid="direct-bill-no" value={billNo} onChange={(e) => setBillNo(e.target.value)}
                    className="w-full h-11 pl-3 pr-24 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 font-mono text-sm" />
                  <button type="button" onClick={fetchSuggested} data-testid="direct-bill-no-suggest"
                    className="absolute right-1 top-1 h-9 px-3 rounded-md text-[11px] font-bold uppercase tracking-wider text-forest hover:bg-[#F9F6F0] inline-flex items-center gap-1">
                    <RefreshCw className="h-3 w-3" strokeWidth={2} /> New
                  </button>
                </div>
              </Field>
              <Field label="Patient name" icon={User}>
                <input data-testid="direct-patient-name" value={patientName} onChange={(e) => setPatientName(e.target.value)}
                  placeholder="Full name"
                  className="w-full h-11 px-3 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40" />
              </Field>
              <Field label="UHID / MRN" icon={Hash}>
                <input data-testid="direct-patient-mrn" value={mrn} onChange={(e) => setMrn(e.target.value)} placeholder="BL######"
                  className="w-full h-11 px-3 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 font-mono uppercase text-sm" />
              </Field>
              <Field label="Phone" icon={Phone}>
                <input data-testid="direct-patient-phone" value={patientPhone} onChange={(e) => setPatientPhone(e.target.value)}
                  placeholder="+91 …"
                  className="w-full h-11 px-3 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40" />
              </Field>
              <Field label="Customer GSTIN (B2B)" icon={Hash}>
                <input data-testid="direct-customer-gstin" value={customerGstin} onChange={(e) => setCustomerGstin(e.target.value)}
                  placeholder="15-char GSTIN"
                  className="w-full h-11 px-3 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 font-mono text-sm uppercase" />
              </Field>
              <Field label="Prescription duration" icon={Clock}>
                <select data-testid="direct-duration" value={duration.label} onChange={(e) => setDuration(DURATIONS.find(d => d.label === e.target.value) || DURATIONS[0])}
                  className="w-full h-11 px-3 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 text-sm">
                  {DURATIONS.map((d) => <option key={d.label} value={d.label}>{d.label}</option>)}
                </select>
              </Field>
              <Field label="Follow-up date" icon={Calendar}>
                <input type="date" data-testid="direct-followup-date" value={followUpDate} onChange={(e) => setFollowUpDate(e.target.value)}
                  className="w-full h-11 px-3 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 text-sm" />
              </Field>
            </div>

            {/* Line items */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold uppercase tracking-wider text-[#5C5652] ornament-dot">Medicines · GST 5% inclusive</div>
                <button onClick={addItem} data-testid="direct-add-item" className="text-sm text-forest font-semibold inline-flex items-center gap-1 hover:underline">
                  <Plus className="h-3.5 w-3.5" strokeWidth={2} /> Add row
                </button>
              </div>
              <div className="space-y-2" data-testid="direct-items">
                <div className="hidden md:grid grid-cols-[2.6fr_0.6fr_1fr_auto] gap-2 text-[9px] uppercase tracking-wider text-[#5C5652] font-bold px-1">
                  <div>Medicine · HSN</div>
                  <div className="text-center">Qty</div>
                  <div className="text-center">MRP incl. GST (₹)</div>
                  <div></div>
                </div>
                {items.map((it, i) => (
                  <div key={i} className="grid grid-cols-2 md:grid-cols-[2.6fr_0.6fr_1fr_auto] gap-2 items-start border md:border-0 border-sand rounded-lg md:rounded-none p-2 md:p-0">
                    <div className="col-span-2 md:col-span-1">
                      <div className="md:hidden text-[9px] uppercase tracking-wider text-[#5C5652] font-bold mb-1">Medicine · HSN</div>
                      <MedicineSearch
                        branch={branch}
                        value={it.name}
                        sku={it.sku}
                        onType={(name) => setItem(i, { name, sku: null })}
                        onPick={(inv) => {
                          setItem(i, { name: inv.name, price: inv.selling_price, sku: inv.sku, hsn: inv.hsn || "" });
                          setItems((prev) => (i === prev.length - 1 ? [...prev, emptyRow()] : prev));
                        }}
                        testid={`direct-name-${i}`}
                      />
                      <input data-testid={`direct-hsn-${i}`} value={it.hsn || ""} onChange={(e) => setItem(i, { hsn: e.target.value })}
                        placeholder="HSN (optional)"
                        className="h-7 mt-1 px-2 w-full rounded bg-white border border-sand focus:outline-none focus:ring-1 focus:ring-[#1A4331]/40 min-w-0 text-[11px] font-mono" />
                    </div>
                    <div>
                      <div className="md:hidden text-[9px] uppercase tracking-wider text-[#5C5652] font-bold mb-1">Qty</div>
                      <input data-testid={`direct-qty-${i}`} type="number" min="1" value={it.qty} onChange={(e) => setItem(i, { qty: e.target.value })}
                        className="h-10 px-2 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 min-w-0 text-center w-full" />
                    </div>
                    <div>
                      <div className="md:hidden text-[9px] uppercase tracking-wider text-[#5C5652] font-bold mb-1">MRP (incl. GST)</div>
                      <input data-testid={`direct-price-${i}`} type="number" min="0" step="0.01" value={it.price}
                        readOnly={!!it.sku} title={it.sku ? "Price locked to inventory MRP (GST 5% included)" : ""}
                        onChange={(e) => setItem(i, { price: e.target.value })}
                        className={`h-10 px-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 min-w-0 text-right w-full ${it.sku ? "bg-[#F0EBDE] border-[#1A4331] text-[#2A2624] cursor-not-allowed" : "bg-white border-sand"}`} />
                    </div>
                    <button onClick={() => removeItem(i)} data-testid={`direct-remove-${i}`}
                      className="h-10 w-full md:w-10 rounded-lg border border-sand text-[#5C5652] hover:bg-[#F9F6F0] flex items-center justify-center">
                      <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Split payments — one or more mode / amount rows */}
            <div className="mt-5" data-testid="direct-payments">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] uppercase tracking-wider text-[#5C5652] flex items-center gap-1">
                  <CreditCard className="h-3.5 w-3.5" strokeWidth={1.75} /> Payment · split allowed
                </label>
                <button type="button" onClick={addPayment} data-testid="direct-add-payment"
                  className="text-xs text-forest font-semibold inline-flex items-center gap-1 hover:underline">
                  <Plus className="h-3 w-3" strokeWidth={2} /> Add mode
                </button>
              </div>
              <div className="space-y-2">
                {payments.map((p, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                    <select value={p.mode} onChange={(e) => setPayment(i, { mode: e.target.value })}
                      data-testid={`direct-pay-mode-${i}`}
                      className="h-10 px-3 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 text-sm">
                      {PAY_MODES.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                    </select>
                    <input type="number" min="0" step="0.01" value={p.amount}
                      onChange={(e) => setPayment(i, { amount: e.target.value })}
                      placeholder={i === 0 ? totals.total.toFixed(2) : "0.00"}
                      data-testid={`direct-pay-amount-${i}`}
                      className="h-10 px-3 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 text-right font-mono text-sm" />
                    <button type="button" onClick={() => removePayment(i)} data-testid={`direct-pay-remove-${i}`}
                      className="h-10 w-10 rounded-lg border border-sand text-[#5C5652] hover:bg-[#F9F6F0] flex items-center justify-center">
                      <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex flex-wrap gap-4 text-[11px] text-[#5C5652]">
                <span>Entered <span className="font-mono font-bold text-forest">{money(paymentsTotal)}</span></span>
                {paymentsRemaining > 0.01 && <span className="text-[#C25946] font-semibold">Short by {money(paymentsRemaining)}</span>}
                {paymentsTotal > totals.total + 0.01 && <span className="text-[#C25946] font-semibold">Over by {money(paymentsTotal - totals.total)} (change)</span>}
              </div>
            </div>

            <textarea data-testid="direct-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              placeholder="Notes (optional)"
              className="w-full mt-4 px-3 py-3 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 resize-none" />

            <div className="mt-4 flex flex-wrap justify-end gap-x-8 gap-y-2 text-sm">
              <div><span className="text-[#5C5652]">Taxable value</span> <span className="font-mono ml-1">{money(totals.taxable - totals.fee)}</span></div>
              <div><span className="text-[#5C5652]">CGST + SGST</span> <span className="font-mono ml-1">{money(totals.cgst + totals.sgst)}</span></div>
              {totals.fee > 0 && <div><span className="text-[#5C5652]">Consultation ({consultFee.type || "new"})</span> <span className="font-mono ml-1">{money(totals.fee)}</span></div>}
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">Total (incl. GST)</div>
                <div className="font-display text-3xl text-forest" data-testid="direct-total">{money(totals.total)}</div>
              </div>
            </div>

            <div className="mt-5 flex gap-3">
              <button onClick={submit} disabled={busy} data-testid="direct-submit"
                className="flex-1 h-12 rounded-lg bg-forest text-white font-semibold hover:bg-forest-hover disabled:opacity-60 inline-flex items-center justify-center gap-2">
                {editingBill ? <Save className="h-4 w-4" strokeWidth={1.75} /> : <Send className="h-4 w-4" strokeWidth={1.75} />}
                {busy ? "Saving…" : (editingBill ? `Save changes · ${money(totals.total)}` : `Save & print · ${money(totals.total)}`)}
              </button>
            </div>
          </div>

          <div className="bg-white border border-sand rounded-lg p-5 h-fit">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#5C5652] mb-3 ornament-dot">
              <ListOrdered className="h-3.5 w-3.5" strokeWidth={2} /> Today&apos;s bills · {recent.length}
            </div>
            {recent.length === 0 ? (
              <div className="text-sm text-[#5C5652]">No direct bills yet today.</div>
            ) : (
              <ul className="divide-y divide-[#E8E3D9]" data-testid="direct-recent-list">
                {recent.map((b) => {
                  const locked = b.archived || b.verified;
                  return (
                  <li key={b.id} className="py-2.5 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-[11px] text-forest truncate flex items-center gap-1">
                        {b.bill_no}
                        {b.bill_type === "courier" && <span className="inline-flex items-center gap-0.5 text-[8px] uppercase tracking-wider bg-[#E5A93D]/20 text-[#8B6A1F] px-1.5 py-0.5 rounded"><Truck className="h-2.5 w-2.5" /> courier</span>}
                        {locked && <Lock className="h-3 w-3 text-[#5C5652]" />}
                      </div>
                      <div className="font-semibold text-[#2A2624] truncate">{b.customer_name}</div>
                      <div className="text-[11px] text-[#5C5652]">{(b.items || []).length} item · {b.mrn || b.customer_phone || "—"}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-forest">{money(b.total)}</div>
                      <div className="flex gap-2 justify-end mt-0.5">
                        {canEditRole && !locked && (
                          <button onClick={() => startEdit(b)} data-testid={`direct-edit-${b.bill_no}`}
                            className="text-[11px] text-[#8B6A1F] font-semibold inline-flex items-center gap-1 hover:underline">
                            <Edit3 className="h-3 w-3" strokeWidth={2} /> Edit
                          </button>
                        )}
                        <button onClick={() => setReceipt(b)} data-testid={`direct-reprint-${b.bill_no}`}
                          className="text-[11px] text-forest font-semibold inline-flex items-center gap-1 hover:underline">
                          <Eye className="h-3 w-3" strokeWidth={2} /> Reprint
                        </button>
                      </div>
                    </div>
                  </li>
                );})}
              </ul>
            )}
          </div>
        </div>
      )}

      {tab === "history" && <BillHistory branch={branch} onOpen={setReceipt} onEdit={startEdit} canEdit={canEditRole} />}

      {receipt && <DirectReceipt bill={receipt} branch={branch} onClose={() => setReceipt(null)} />}
    </div>
  );
}

function Field({ label, icon: Icon, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-[10px] uppercase tracking-wider text-[#5C5652] mb-2 flex items-center gap-1">
        {Icon && <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />} {label}
      </label>
      {children}
    </div>
  );
}

/** Combobox — type to search inventory inline. No lens icon. */
function MedicineSearch({ branch, value, sku, onType, onPick, testid }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hover, setHover] = useState(-1);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!branch?.code || !open) return;
    const q = (value || "").trim();
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/inventory", { params: { branch_code: branch.code, q: q || undefined } });
        setRows(data.slice(0, 30));
      } catch { setRows([]); }
      finally { setLoading(false); }
    }, 180);
    return () => clearTimeout(t);
  }, [value, open, branch?.code]);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHover((h) => Math.min(h + 1, rows.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHover((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && hover >= 0 && rows[hover]) { e.preventDefault(); onPick(rows[hover]); setOpen(false); setHover(-1); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => { onType(e.target.value); setOpen(true); setHover(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        placeholder="Type medicine name — results appear as you type"
        className={`h-10 px-3 w-full rounded-lg bg-white border ${sku ? "border-[#1A4331]" : "border-sand"} focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 min-w-0`}
        autoComplete="off"
      />
      {sku && <span className="absolute right-2 top-3 text-[9px] font-bold text-forest">📦</span>}
      {open && (loading || rows.length > 0) && (
        <div className="absolute z-40 top-full left-0 right-0 mt-1 bg-white border border-sand rounded-lg shadow-lg max-h-72 overflow-y-auto" data-testid={`${testid}-dropdown`}>
          {loading && <div className="px-3 py-2 text-xs text-[#5C5652]">Searching…</div>}
          {rows.map((r, idx) => (
            <button
              key={r.id}
              type="button"
              onClick={() => { onPick(r); setOpen(false); setHover(-1); }}
              disabled={r.stock_qty <= 0}
              data-testid={`${testid}-opt-${r.sku}`}
              className={`w-full text-left px-3 py-2 flex items-center gap-3 disabled:opacity-40 disabled:cursor-not-allowed ${idx === hover ? "bg-[#F0EBDE]" : "hover:bg-[#F9F6F0]"}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#2A2624] truncate text-sm">{r.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">{r.sku} · per {r.unit} · Stock {r.stock_qty}</div>
              </div>
              <div className="text-right">
                <div className="text-xs font-mono text-forest">₹ {Number(r.selling_price).toFixed(2)}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function BillHistory({ branch, onOpen, onEdit, canEdit }) {
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = useCallback(async () => {
    if (!branch?.code) return;
    setLoading(true);
    try {
      const params = { branch_code: branch.code, include_archived: true, limit: 500 };
      if (q.trim()) params.q = q.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/direct-bills", { params });
      setRows(data);
    } catch (err) { toast.error(formatApiError(err, "Search failed")); }
    finally { setLoading(false); }
  }, [branch?.code, q, dateFrom, dateTo]);

  useEffect(() => { search(); }, [search]);

  return (
    <div className="bg-white border border-sand rounded-lg p-5" data-testid="direct-history">
      <div className="grid gap-3 md:grid-cols-[2fr_1fr_1fr_auto] items-end mb-4">
        <Field label="Search" icon={Search}>
          <input data-testid="direct-history-q" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="UHID / patient / phone / bill number"
            className="w-full h-10 px-3 rounded-lg border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40" />
        </Field>
        <Field label="From" icon={Calendar}>
          <input type="date" data-testid="direct-history-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="w-full h-10 px-3 rounded-lg border border-sand text-sm" />
        </Field>
        <Field label="To" icon={Calendar}>
          <input type="date" data-testid="direct-history-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="w-full h-10 px-3 rounded-lg border border-sand text-sm" />
        </Field>
        <button onClick={search} data-testid="direct-history-search" className="h-10 px-4 rounded-lg bg-forest text-white text-sm font-semibold hover:bg-forest-hover inline-flex items-center gap-2">
          <Search className="h-3.5 w-3.5" strokeWidth={1.75} /> Search
        </button>
      </div>
      <div className="overflow-x-auto border border-sand rounded">
        <table className="w-full text-sm">
          <thead className="bg-[#F9F6F0] text-[10px] uppercase tracking-wider text-[#5C5652]">
            <tr>
              <th className="text-left px-3 py-2">Bill no</th>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Patient</th>
              <th className="text-left px-3 py-2">UHID</th>
              <th className="text-right px-3 py-2">Amount</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E8E3D9]">
            {loading ? <tr><td colSpan={6} className="px-3 py-6 text-center text-[#5C5652]">Loading…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={6} className="px-3 py-6 text-center text-[#5C5652]">No bills match your search.</td></tr>
              : rows.map((b) => {
                  const locked = b.archived || b.verified;
                  return (
                <tr key={b.id} data-testid={`direct-history-row-${b.bill_no}`}>
                  <td className="px-3 py-2 font-mono text-xs text-forest">{b.bill_no}{b.bill_type === "courier" && <span className="ml-1 text-[8px] uppercase tracking-wider text-[#8B6A1F]">· courier</span>}</td>
                  <td className="px-3 py-2 text-xs">{new Date(b.generated_at).toLocaleString()}</td>
                  <td className="px-3 py-2">{b.customer_name}<br/><span className="text-[10px] text-[#5C5652]">{b.customer_phone || "—"}</span></td>
                  <td className="px-3 py-2 font-mono text-xs">{b.mrn || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">{money(b.total)}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      {canEdit && !locked && (
                        <button onClick={() => onEdit(b)} data-testid={`direct-history-edit-${b.bill_no}`}
                          className="h-8 px-3 rounded border border-[#E5A93D] text-xs font-semibold text-[#8B6A1F] hover:bg-[#FFF7E3] inline-flex items-center gap-1">
                          <Edit3 className="h-3 w-3" strokeWidth={1.75} /> Edit
                        </button>
                      )}
                      <button onClick={() => onOpen(b)} data-testid={`direct-history-reprint-${b.bill_no}`}
                        className="h-8 px-3 rounded border border-sand text-xs font-semibold hover:bg-[#F9F6F0] inline-flex items-center gap-1">
                        <Printer className="h-3 w-3" strokeWidth={1.75} /> Reprint
                      </button>
                    </div>
                  </td>
                </tr>
                );})}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DirectReceipt({ bill: initialBill, branch, onClose }) {
  const [bill, setBill] = useState(initialBill);
  const [mode, setMode] = useState("thermal");
  const [einvoiceOpen, setEinvoiceOpen] = useState(false);
  const thermalRef = useRef(null);
  const a4Ref = useRef(null);
  const doPrint = () => {
    const target = mode === "a4" ? a4Ref.current : thermalRef.current;
    printNode(target, { title: `Bill ${bill.bill_no}`, pageSize: mode === "a4" ? "a4" : "thermal80" });
  };
  const items = bill.items || [];
  const dateStr = new Date(bill.generated_at).toLocaleString();
  const billPayments = (bill.payments && bill.payments.length) ? bill.payments : [{ mode: bill.payment_mode || "cash", amount: bill.paid ?? bill.total }];
  const paidTotal = billPayments.reduce((s, p) => s + Number(p.amount || 0), 0);
  const change = Math.max(0, Number(paidTotal) - Number(bill.total));
  const qrPayload = bill.signed_qr || `IRN:${bill.irn || "PENDING"}|GSTIN:${bill.customer_gstin || ""}|INV:${bill.bill_no}|DT:${(bill.generated_at || "").slice(0, 10)}|AMT:${Number(bill.total || 0).toFixed(2)}`;

  return (
    <Portal>
      <div className="fixed inset-0 bg-[#2A2624]/60 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4" onClick={onClose} data-testid="direct-receipt">
        <div className="bg-white w-full max-w-2xl rounded-lg border border-sand overflow-hidden animate-fade-up flex flex-col max-h-[95vh]" onClick={(e) => e.stopPropagation()}>
          <div className="px-4 sm:px-5 py-3 border-b border-sand flex flex-wrap items-center justify-between gap-2 shrink-0">
            <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">Bill preview · {bill.bill_no}</div>
            <div className="flex flex-wrap gap-2 items-center">
              <div className="inline-flex rounded-md border border-sand overflow-hidden">
                <button type="button" onClick={() => setMode("thermal")} data-testid="receipt-mode-thermal"
                  className={`px-3 h-8 text-xs font-semibold ${mode === "thermal" ? "bg-forest text-white" : "bg-white text-[#5C5652] hover:bg-[#F9F6F0]"}`}>Thermal 80mm</button>
                <button type="button" onClick={() => setMode("a4")} data-testid="receipt-mode-a4"
                  className={`px-3 h-8 text-xs font-semibold border-l border-sand ${mode === "a4" ? "bg-forest text-white" : "bg-white text-[#5C5652] hover:bg-[#F9F6F0]"}`}>A4 tax invoice</button>
              </div>
              <button type="button" onClick={() => setEinvoiceOpen(true)} data-testid="receipt-einvoice"
                className="h-8 px-3 rounded-md border border-sand text-xs font-semibold hover:bg-[#F9F6F0] inline-flex items-center gap-1">
                <QrCode className="h-3.5 w-3.5" strokeWidth={1.75} /> {bill.irn ? "Update IRN" : "Add IRN"}
              </button>
            </div>
          </div>

          <div className="overflow-y-auto">
            {/* Thermal 80mm — bigger bold fonts + brand logo */}
            <div ref={thermalRef} className={`px-3 sm:px-5 py-4 ${mode === "thermal" ? "block" : "hidden"}`} data-testid="thermal-bill">
              <div className="mx-auto max-w-[300px] font-mono text-[13px] leading-snug text-[#000] font-bold">
                <div className="text-center border-b border-dashed border-[#000] pb-2 mb-2">
                  <div className="flex items-center justify-center mb-1">
                    <BrandLogo size="h-10" className="mx-auto" />
                  </div>
                  <div className="font-bold text-[13px] leading-tight">BADARINATH AYURVEDA HOSPITAL &amp; RESEARCH CENTER PVT LTD</div>
                  <div className="text-[11px] leading-tight mt-1">Parambussery, Kurumassery (PO), Alwaye — 683 579</div>
                  <div className="text-[11px] leading-tight">Contact: +91 7907986498 · +91 8848256096</div>
                  <div className="text-[12px] uppercase mt-1">{branch?.name || bill.branch_code}</div>
                  <div className="text-[12px] mt-1 uppercase">{(bill.bill_type || "direct") === "courier" ? "COURIER BILL" : "CASH / DIRECT BILL"}</div>
                </div>
                <div className="text-[12px]">Bill: <b>{bill.bill_no}</b></div>
                <div className="text-[12px]">Date: {dateStr}</div>
                <div className="text-[12px]">Patient: {bill.customer_name}</div>
                {bill.mrn && <div className="text-[12px]">UHID: {bill.mrn}</div>}
                {bill.customer_phone && <div className="text-[12px]">Ph: {bill.customer_phone}</div>}
                {bill.follow_up_date && <div className="text-[12px]">Review: {bill.follow_up_date} ({bill.duration_label || "—"})</div>}
                <div className="border-t border-dashed border-[#000] my-2"></div>
                <div className="flex text-[12px] font-bold">
                  <span className="flex-1">ITEM</span>
                  <span className="w-8 text-right">QTY</span>
                  <span className="w-14 text-right">MRP</span>
                  <span className="w-16 text-right">AMT</span>
                </div>
                <div className="border-t border-dashed border-[#000] my-1"></div>
                {items.map((it, i) => (
                  <div key={i} className="text-[12px]">
                    <div className="truncate">{it.name}{it.sku ? ` (${it.sku})` : ""}</div>
                    <div className="flex">
                      <span className="flex-1"></span>
                      <span className="w-8 text-right">{it.qty}</span>
                      <span className="w-14 text-right">{Number(it.price).toFixed(2)}</span>
                      <span className="w-16 text-right">{(Number(it.qty) * Number(it.price)).toFixed(2)}</span>
                    </div>
                  </div>
                ))}
                <div className="border-t border-dashed border-[#000] my-2"></div>
                <RowMono label="Taxable" value={money(bill.subtotal)} />
                <RowMono label="CGST" value={money(bill.cgst_total)} />
                <RowMono label="SGST" value={money(bill.sgst_total)} />
                <div className="flex justify-between text-[14px] font-bold border-t border-b border-[#000] py-1 my-1">
                  <span>TOTAL (incl. GST)</span>
                  <span>{money(bill.total)}</span>
                </div>
                {billPayments.map((p, i) => (
                  <RowMono key={i} label={`Paid · ${p.mode}`} value={money(p.amount)} />
                ))}
                {change > 0 && <RowMono label="Change" value={money(change)} />}
                <div className="border-t border-dashed border-[#000] my-2"></div>
                <div className="text-[11px] text-center leading-tight font-bold">
                  <div>ശ്രദ്ധിക്കുക / NOTICE</div>
                  <div>ഡോക്ടറുടെ നിർദ്ദേശപ്രകാരം, ആവശ്യമുള്ള കാലയളവിലേക്ക് മാത്രം മരുന്നുകൾ വാങ്ങുക — മരുന്നുകൾ തിരികെ എടുക്കുന്നതല്ല</div>
                  <div className="mt-1">Kindly buy medicines only for the duration prescribed by the doctor. Medicines once sold will not be taken back.</div>
                </div>
                <div className="text-center text-[12px] mt-2 font-bold">
                  <div>Cashier: {bill.generated_by_name || bill.generated_by}</div>
                </div>
                <div className="border border-[#000] rounded p-1.5 mt-2 text-[11px] leading-tight font-bold">
                  <div className="text-center uppercase tracking-wider mb-1">Our branches</div>
                  <div>• Kurumassery, Aluva</div>
                  <div>• Mudappallur, Palakkad</div>
                  <div>• South Vazhakulam, Perumbavoor</div>
                </div>
              </div>
            </div>

            {/* A4 detailed tax invoice */}
            <div ref={a4Ref} className={`p-4 sm:p-6 ${mode === "a4" ? "block" : "hidden"}`} data-testid="a4-bill">
              <div className="border-b-2 border-forest pb-4 mb-4 flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="font-display text-2xl text-forest leading-tight">Badarinath Ayurveda Hospital &amp; Research Center Pvt Ltd</div>
                  <div className="text-xs text-[#5C5652] mt-1 leading-snug">
                    Parambussery, Kurumassery (PO), Alwaye, India — 683 579<br />
                    Contact: <span className="font-mono">+91 7907986498</span> · <span className="font-mono">+91 8848256096</span>
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-[#5C5652] mt-2">
                    Branch: {branch?.name || bill.branch_code} · {(bill.bill_type || "direct") === "courier" ? "Courier Tax Invoice" : "Retail Tax Invoice"} · GST Inclusive
                  </div>
                </div>
                <div className="text-right text-xs shrink-0">
                  <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">Bill no</div>
                  <div className="font-mono text-forest font-bold">{bill.bill_no}</div>
                  <div className="mt-2 text-[10px] uppercase tracking-wider text-[#5C5652]">Date</div>
                  <div className="font-mono">{dateStr}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-xs">
                <div><div className="text-[10px] uppercase tracking-wider text-[#5C5652]">Patient</div><div className="font-semibold">{bill.customer_name}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-[#5C5652]">UHID</div><div className="font-mono">{bill.mrn || "—"}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-[#5C5652]">Phone</div><div className="font-mono">{bill.customer_phone || "—"}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-[#5C5652]">Follow-up</div><div className="font-mono">{bill.follow_up_date || "—"}<div className="text-[9px] text-[#5C5652]">{bill.duration_label || ""}</div></div></div>
                {bill.customer_gstin && (
                  <div className="col-span-2 sm:col-span-4"><div className="text-[10px] uppercase tracking-wider text-[#5C5652]">Customer GSTIN</div><div className="font-mono">{bill.customer_gstin}</div></div>
                )}
              </div>

              <table className="w-full text-xs border border-sand rounded overflow-hidden">
                <thead className="bg-[#F9F6F0] text-[10px] uppercase tracking-wider text-[#5C5652]">
                  <tr>
                    <th className="text-left px-2 py-2 w-8">#</th>
                    <th className="text-left px-2 py-2">Medicine</th>
                    <th className="text-left px-2 py-2 w-16">HSN</th>
                    <th className="text-right px-2 py-2 w-10">Qty</th>
                    <th className="text-right px-2 py-2 w-16">MRP</th>
                    <th className="text-right px-2 py-2 w-20">Taxable</th>
                    <th className="text-right px-2 py-2 w-16">CGST</th>
                    <th className="text-right px-2 py-2 w-16">SGST</th>
                    <th className="text-right px-2 py-2 w-20">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E8E3D9]">
                  {items.map((it, i) => {
                    const gross = Number(it.qty || 0) * Number(it.price || 0);
                    const taxable = Number(it.taxable ?? (gross / (1 + Number(it.gst_pct || 0) / 100)));
                    const cgst = Number(it.cgst ?? (gross - taxable) / 2);
                    const sgst = Number(it.sgst ?? cgst);
                    return (
                      <tr key={i}>
                        <td className="px-2 py-2 text-[#5C5652]">{i + 1}</td>
                        <td className="px-2 py-2">{it.name}<div className="text-[9px] font-mono text-[#5C5652]">{it.sku || ""}</div></td>
                        <td className="px-2 py-2 font-mono text-[10px]">{it.hsn || "—"}</td>
                        <td className="px-2 py-2 text-right font-mono">{it.qty}</td>
                        <td className="px-2 py-2 text-right font-mono">{Number(it.price).toFixed(2)}</td>
                        <td className="px-2 py-2 text-right font-mono">{taxable.toFixed(2)}</td>
                        <td className="px-2 py-2 text-right font-mono">{cgst.toFixed(2)}</td>
                        <td className="px-2 py-2 text-right font-mono">{sgst.toFixed(2)}</td>
                        <td className="px-2 py-2 text-right font-mono">{gross.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-[#F9F6F0]">
                  <tr><td colSpan={8} className="px-2 py-2 text-right text-[10px] uppercase tracking-wider text-[#5C5652]">Taxable value</td><td className="px-2 py-2 text-right font-mono">{Number(bill.subtotal).toFixed(2)}</td></tr>
                  <tr><td colSpan={8} className="px-2 py-2 text-right text-[10px] uppercase tracking-wider text-[#5C5652]">Total CGST</td><td className="px-2 py-2 text-right font-mono">{Number(bill.cgst_total || 0).toFixed(2)}</td></tr>
                  <tr><td colSpan={8} className="px-2 py-2 text-right text-[10px] uppercase tracking-wider text-[#5C5652]">Total SGST</td><td className="px-2 py-2 text-right font-mono">{Number(bill.sgst_total || 0).toFixed(2)}</td></tr>
                  <tr className="border-t border-forest"><td colSpan={8} className="px-2 py-2 text-right font-bold text-forest">Grand total (incl. GST)</td><td className="px-2 py-2 text-right font-mono font-bold text-forest">{Number(bill.total).toFixed(2)}</td></tr>
                  <tr><td colSpan={8} className="px-2 py-2 text-right text-[10px] uppercase tracking-wider text-[#5C5652]">Payments</td><td className="px-2 py-2 text-right font-mono">{billPayments.map((p, i) => (<div key={i}>{p.mode}: {Number(p.amount).toFixed(2)}</div>))}</td></tr>
                  {change > 0 && <tr><td colSpan={8} className="px-2 py-2 text-right text-[10px] uppercase tracking-wider text-[#5C5652]">Change</td><td className="px-2 py-2 text-right font-mono">{change.toFixed(2)}</td></tr>}
                </tfoot>
              </table>

              {bill.notes && (
                <div className="mt-3 text-xs"><div className="text-[10px] uppercase tracking-wider text-[#5C5652]">Notes</div><div>{bill.notes}</div></div>
              )}

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 items-start border-t border-sand pt-4">
                <div className="text-xs space-y-1">
                  <div className="text-[10px] uppercase tracking-wider text-[#5C5652] font-bold">e-Invoice</div>
                  <div><span className="text-[#5C5652]">IRN:</span> <span className="font-mono break-all">{bill.irn || "— pending —"}</span></div>
                  {bill.ack_no && <div><span className="text-[#5C5652]">Ack No:</span> <span className="font-mono">{bill.ack_no}</span></div>}
                  {bill.ack_date && <div><span className="text-[#5C5652]">Ack Dt:</span> <span className="font-mono">{bill.ack_date}</span></div>}
                </div>
                <div className="flex flex-col items-center">
                  <div className="p-2 bg-white border border-sand rounded">
                    <QRCodeSVG value={qrPayload} size={108} level="M" includeMargin={false} />
                  </div>
                  <div className="text-[9px] uppercase tracking-wider text-[#5C5652] mt-1">Scan · GST QR</div>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-4 text-xs">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">Cashier</div>
                  <div>{bill.generated_by_name || bill.generated_by || "—"}</div>
                </div>
                <div className="text-right">
                  <div className="h-10"></div>
                  <div className="border-t border-[#2A2624] pt-1 inline-block min-w-[140px] text-[10px] uppercase tracking-wider text-[#5C5652]">Authorised signatory</div>
                </div>
              </div>

              {/* Bilingual footer notice — required on every printed bill */}
              <div className="mt-6 pt-3 border-t-2 border-forest">
                <div className="text-center text-[11px] font-bold text-forest tracking-wider">ശ്രദ്ധിക്കുക / NOTICE</div>
                <div className="text-center text-[11px] mt-1 leading-snug">
                  ഡോക്ടറുടെ നിർദ്ദേശപ്രകാരം, ആവശ്യമുള്ള കാലയളവിലേക്ക് മാത്രം മരുന്നുകൾ വാങ്ങുക — മരുന്നുകൾ തിരികെ എടുക്കുന്നതല്ല
                </div>
                <div className="text-center text-[11px] mt-1 italic leading-snug">
                  Kindly buy medicines only for the duration prescribed by the doctor. Medicines once sold will not be taken back.
                </div>
              </div>

              {/* Branches box */}
              <div className="mt-5 border border-forest rounded-lg p-3">
                <div className="text-center text-[10px] uppercase tracking-[0.28em] text-forest font-bold mb-2">Our branches</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-center">
                  <div><span className="font-semibold">Kurumassery,</span> Aluva</div>
                  <div><span className="font-semibold">Mudappallur,</span> Palakkad</div>
                  <div><span className="font-semibold">South Vazhakulam,</span> Perumbavoor</div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 flex gap-3 border-t border-sand shrink-0">
            <button onClick={doPrint} data-testid="direct-receipt-print" className="flex-1 h-11 rounded-lg bg-forest text-white font-semibold hover:bg-forest-hover inline-flex items-center justify-center gap-2">
              <Printer className="h-4 w-4" strokeWidth={1.75} /> Print {mode === "thermal" ? "thermal" : "A4"}
            </button>
            <button onClick={onClose} data-testid="direct-receipt-close" className="flex-1 h-11 rounded-lg border border-sand font-semibold hover:bg-[#F9F6F0]">Close</button>
          </div>
        </div>

        {einvoiceOpen && (
          <EInvoiceModal bill={bill} onClose={() => setEinvoiceOpen(false)} onSaved={(u) => { setBill(u); setEinvoiceOpen(false); toast.success("IRN saved on bill"); }} />
        )}
      </div>
    </Portal>
  );
}

function EInvoiceModal({ bill, onClose, onSaved }) {
  const [irn, setIrn] = useState(bill.irn || "");
  const [ackNo, setAckNo] = useState(bill.ack_no || "");
  const [ackDate, setAckDate] = useState(bill.ack_date || "");
  const [signedQr, setSignedQr] = useState(bill.signed_qr || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!irn.trim()) { toast.error("IRN is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/direct-bills/${bill.id}/e-invoice`, { irn: irn.trim(), ack_no: ackNo.trim(), ack_date: ackDate.trim(), signed_qr: signedQr.trim() });
      onSaved(data);
    } catch (err) { toast.error(formatApiError(err, "Save failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-[#2A2624]/70 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-lg border border-sand p-5" onClick={(e) => e.stopPropagation()} data-testid="einvoice-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-xl text-forest">e-Invoice · IRN &amp; QR</div>
          <button onClick={onClose} className="h-8 w-8 rounded-md border border-sand hover:bg-[#F9F6F0] flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>
        <p className="text-[11px] text-[#5C5652] mb-3">Paste values returned from the GST portal. Signed QR (JWT) is optional; a fallback string is used if empty.</p>
        <div className="space-y-3">
          <input data-testid="einvoice-irn" value={irn} onChange={(e) => setIrn(e.target.value)} placeholder="IRN (64-char hash)"
            className="w-full h-10 px-3 rounded-lg border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 font-mono text-xs" />
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="einvoice-ack" value={ackNo} onChange={(e) => setAckNo(e.target.value)} placeholder="Ack No"
              className="h-10 px-3 rounded-lg border border-sand font-mono text-xs" />
            <input data-testid="einvoice-ackdate" value={ackDate} onChange={(e) => setAckDate(e.target.value)} placeholder="Ack Date YYYY-MM-DD"
              className="h-10 px-3 rounded-lg border border-sand font-mono text-xs" />
          </div>
          <textarea data-testid="einvoice-qr" value={signedQr} onChange={(e) => setSignedQr(e.target.value)} rows={3}
            placeholder="Signed QR (optional)" className="w-full px-3 py-2 rounded-lg border border-sand font-mono text-[10px] resize-none" />
        </div>
        <div className="mt-5 flex gap-3">
          <button onClick={save} disabled={busy} data-testid="einvoice-save" className="flex-1 h-11 rounded-lg bg-forest text-white font-semibold hover:bg-forest-hover disabled:opacity-60">
            {busy ? "Saving…" : "Save IRN"}
          </button>
          <button onClick={onClose} className="h-11 px-4 rounded-lg border border-sand font-semibold hover:bg-[#F9F6F0]">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function RowMono({ label, value, bold }) {
  return (
    <div className={`flex justify-between text-[10px] ${bold ? "font-bold" : ""}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
